// The words and tones the calendar wears — one place, so the timeline, the
// panel lists and the details always say the same thing about the same state.
//
// Tones are the live map's (`Tone` in LiveMapCards), worn the same way: in
// session is the map's "at client" green, on a trip its "on the road" blue,
// free its "active" teal. A colour means one thing on both screens.

import type { CalendarDispatchLegKind } from "@bednbody/types";
import type { Tone } from "../live-map/LiveMapCards";
import { type BlockPhase, clockLabel, type PersonStatus, shortClock } from "./calendar-board";

export const LEG_LABEL: Record<CalendarDispatchLegKind, string> = {
  drop_off: "Drop-off",
  pickup: "Pickup",
  return: "Back to hub",
};

/** The one-word form a short timeline block has room for. */
export const LEG_SHORT: Record<CalendarDispatchLegKind, string> = {
  drop_off: "Drop",
  pickup: "Pick",
  return: "Hub",
};

export const PHASE_LABEL: Record<BlockPhase, string> = {
  done: "Done",
  now: "Happening now",
  next: "Coming up",
  unassigned: "Needs a therapist",
};

export const PHASE_TONE: Record<BlockPhase, Tone> = {
  done: "muted",
  now: "success",
  next: "primary",
  unassigned: "danger",
};

export function personStatusLabel(status: PersonStatus): string {
  switch (status.kind) {
    case "day_off":
      return "Day off";
    case "scheduled":
      return "On shift";
    case "not_clocked_in":
      return "Not clocked in";
    case "in_session":
      return `In session · until ${shortClock(status.untilMin)}`;
    case "on_trip":
      return `On a trip · back ${shortClock(status.untilMin)}`;
    case "free":
      return status.nextMin === null ? "Free" : `Free · next ${clockLabel(status.nextMin)}`;
    case "sos":
      return "SOS";
    case "disconnected":
      return "Offline";
    case "on_break":
      return "On break";
    case "waiting_pickup":
      return "Waiting for pickup";
  }
}

export function personStatusTone(status: PersonStatus): Tone {
  switch (status.kind) {
    case "day_off":
    case "scheduled":
      return "muted";
    case "not_clocked_in":
      return "warning";
    case "in_session":
      return "success";
    case "on_trip":
      return "info";
    case "free":
      return "primary";
    case "sos":
    case "disconnected":
      return "danger";
    case "on_break":
      return "gold";
    case "waiting_pickup":
      return "warning";
  }
}

export function bookingKindLabel(kind: "asap" | "scheduled" | "unknown"): string {
  return kind === "asap" ? "ASAP" : kind === "scheduled" ? "Scheduled" : "Booking";
}

const DAY_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  weekday: "short",
  month: "short",
  day: "numeric",
});

/** "Wed Oct 21" — read from the date itself, so no time zone can shift it. */
export function dayLabel(day: string): string {
  return DAY_PARTS.format(new Date(`${day}T12:00:00Z`)).replace(",", "");
}

/** "Mon 19" — a week column's head. */
export function weekdayLabel(day: string): string {
  const [weekday] = dayLabel(day).split(" ");
  return `${weekday} ${Number(day.slice(8, 10))}`;
}

/** The header's date button: "Today, Wed Oct 21", or the week it is in. */
export function boardDateTitle(
  view: "day" | "week",
  day: string,
  today: string,
  week: readonly string[],
) {
  if (view === "day") return day === today ? `Today, ${dayLabel(day)}` : dayLabel(day);
  const first = week[0] ?? day;
  const last = week.at(-1) ?? day;
  const range = `${dayLabel(first).slice(4)} – ${
    first.slice(5, 7) === last.slice(5, 7) ? Number(last.slice(8, 10)) : dayLabel(last).slice(4)
  }`;
  return week.includes(today) ? `This week, ${range}` : range;
}
